import { createContext, useContext, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";

type Language = "en" | "ar";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (en: string, ar?: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { t: translate, i18n: i18nInstance } = useTranslation();

  const language = (i18nInstance.language as Language) || "en";
  const isRTL = language === "ar";

  // Update HTML attributes when language changes
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [language, isRTL]);

  const setLanguage = (lang: Language) => {
    i18nInstance.changeLanguage(lang);
  };

  // Backward-compatible t function:
  // - If 2 arguments (en, ar): use inline translation (legacy pattern)
  // - If 1 argument: use as i18next key
  const t = (en: string, ar?: string): string => {
    if (ar !== undefined) {
      // Legacy inline translation pattern
      return language === "ar" ? ar : en;
    }
    // i18next key-based translation
    return translate(en);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
